/**
 * Раскладка слов по местам — касанием («клик-клик») и перетаскиванием.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНО ОТ БЛОКОВ. «Два раза перенёс — не засчиталось» (10.09.2026):
 * касание слова клало его в ПЕРВЫЙ пустой пропуск, а не туда, куда смотрел
 * ученик. На телефоне перетаскивания нет, касание — единственный путь, и ученик,
 * начавший не с первого пропуска, получал «верные слова не на местах». На копии
 * боевой базы так выглядели все 74 неверных ответа из 1251 в режиме
 * перетаскивания. У колонок касание не делало вообще ничего.
 *
 * ⚠️ КАСАНИЕ И ПЕРЕТАСКИВАНИЕ ИДУТ ЧЕРЕЗ ОДИН `move`. Два пути к одному
 * состоянию, написанные врозь, уже расходились: бросок слова в его же пропуск
 * стирал слово, потому что проверка «слово уже где-то лежит» стояла раньше
 * проверки «это то самое место».
 *
 * ⚠️ СЛОВА РАЗЛИЧАЮТСЯ ПО НОМЕРУ, А НЕ ПО ТЕКСТУ. В колонках встречается одно и
 * то же слово дважды («berry»); по тексту два таких слова переезжали бы вместе.
 *
 * Семантика касаний та же, что у сопоставления (`lib/matching.ts`): касание
 * слова, уже стоящего на месте, выделяет его там же — так его можно переставить.
 */

/** Где лежит каждое слово: номер места или `null` — в банке. Индекс — номер слова. */
export type Placement = {
  at: (number | null)[];
  /** Выделенное слово, ждущее места. */
  sel: number | null;
};

/** Сколько слов вмещает место: пропуск и картинка — одно, колонка — сколько угодно. */
export type Capacity = 1 | typeof Infinity;

export const emptyPlacement = (items: number): Placement => ({ at: Array(items).fill(null), sel: null });

/** Кто стоит на месте с вместимостью 1; -1 — место свободно. */
export const occupant = (s: Placement, place: number): number => s.at.indexOf(place);

/** Номера слов на месте, по порядку банка. */
export const itemsAt = (s: Placement, place: number | null): number[] =>
  s.at.flatMap((p, i) => (p === place ? [i] : []));

/**
 * Переложить слово на место (`null` — вернуть в банк). Общий шаг для касания и
 * перетаскивания. Занятое место с вместимостью 1 отдаёт прежнее слово в банк.
 * Выделение после любого переноса снимается.
 */
export function move(s: Placement, item: number, place: number | null, capacity: Capacity): Placement {
  if (item < 0 || item >= s.at.length) return s;
  const at = [...s.at];
  if (place !== null && capacity === 1) {
    for (let i = 0; i < at.length; i++) if (i !== item && at[i] === place) at[i] = null;
  }
  at[item] = place;
  return { at, sel: null };
}

/** Касание слова (в банке или на месте): выделить; повторное касание снимает выделение. */
export function tapItem(s: Placement, item: number): Placement {
  if (item < 0 || item >= s.at.length) return s;
  return { at: s.at, sel: s.sel === item ? null : item };
}

/** Касание места: выделенное слово переезжает сюда. Без выделения — ничего. */
export function tapPlace(s: Placement, place: number, capacity: Capacity): Placement {
  return s.sel === null ? s : move(s, s.sel, place, capacity);
}

/**
 * Касание места с вместимостью 1, которое само показывает слово (пропуск,
 * подпись под картинкой). Есть выделение другого слова — кладём его сюда;
 * иначе касание выделяет слово, стоящее на месте.
 */
export function tapSlot(s: Placement, place: number): Placement {
  const occ = occupant(s, place);
  if (s.sel !== null && s.sel !== occ) return move(s, s.sel, place, 1);
  return occ >= 0 ? tapItem(s, occ) : s;
}

/** Касание банка: выделенное слово возвращается в банк. */
export function tapBank(s: Placement): Placement {
  return s.sel === null ? s : move(s, s.sel, null, Infinity);
}

/**
 * Метка перетаскивания. В уроке несколько блоков, и слово, брошенное в чужой
 * блок, не должно там появиться: номер слова имеет смысл только в своём.
 */
export const dragToken = (scope: string, item: number) => `ob-place:${scope}:${item}`;

/** Номер слова из метки своего блока; `null` — чужая метка или не метка вовсе. */
export function readDragToken(scope: string, token: string): number | null {
  const prefix = `ob-place:${scope}:`;
  if (!token.startsWith(prefix)) return null;
  const n = Number(token.slice(prefix.length));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/**
 * Перестановка строк касанием (порядок абзацев): первое касание выделяет строку,
 * касание другой строки ставит выделенную на её место.
 */
export function tapRow(order: number[], sel: number | null, row: number): { order: number[]; sel: number | null } {
  if (row < 0 || row >= order.length) return { order, sel };
  if (sel === null) return { order, sel: row };
  if (sel === row) return { order, sel: null };
  const next = [...order];
  const [moved] = next.splice(sel, 1);
  next.splice(row, 0, moved);
  return { order: next, sel: null };
}
